import React from "react";
import { Link } from "react-router-dom";
import { Answer, Enrolment } from "../../../../stores/EnrolmentStore/Types";
import {
  QuestionPreview,
  TopicPreview,
} from "../../../../stores/CoursePreviewStore/Types";
import { ActiveTab } from "../types";
import {
  lessonState,
  lessonStatusLabel,
  minutesLabel,
  questionPath,
  translatedDescription,
  translatedName,
} from "../helpers";

interface Props {
  activeTab: ActiveTab;
  answers: Answer[];
  chapters: TopicPreview[];
  currentUser: any;
  enrolment: Enrolment;
  loading: boolean;
}

const LessonIcon = ({
  enrolment,
  questionNumber,
  state,
}: {
  enrolment: Enrolment;
  questionNumber: number;
  state: string;
}) => {
  if (state === "current") {
    return (
      <span className="lava">
        {enrolment?.user?.profile_picture_url ? (
          <img src={enrolment.user.profile_picture_url} alt="" loading="lazy" />
        ) : (
          <span>{questionNumber}</span>
        )}
      </span>
    );
  }

  if (state === "passed") {
    return (
      <>
        <span className="lnum">{questionNumber}</span>
        <span className="lic">
          <svg aria-hidden="true">
            <use href="#ic-check" />
          </svg>
        </span>
      </>
    );
  }

  if (state === "await") {
    return (
      <>
        <span className="lnum">{questionNumber}</span>
        <span className="lic">
          <svg aria-hidden="true">
            <use href="#ic-clock" />
          </svg>
        </span>
        <span className="lic-done">
          <svg aria-hidden="true">
            <use href="#ic-check" />
          </svg>
        </span>
      </>
    );
  }

  if (state === "failed") {
    return (
      <>
        <span className="lnum">{questionNumber}</span>
        <span className="lic">
          <svg aria-hidden="true">
            <use href="#ic-retry" />
          </svg>
        </span>
      </>
    );
  }

  return (
    <>
      <span className="lnum">{questionNumber}</span>
      <span className="lic-done">
        <svg aria-hidden="true">
          <use href="#ic-check" />
        </svg>
      </span>
    </>
  );
};

const Lesson = ({
  answers,
  currentUser,
  enrolment,
  index,
  locked,
  question,
}: {
  answers: Answer[];
  currentUser: any;
  enrolment: Enrolment;
  index: number;
  locked: boolean;
  question: QuestionPreview;
}) => {
  const state = lessonState(enrolment, question, answers);
  const answer = answers.find((item) => item.question_id == question.id);
  const lessonLocked =
    locked && !["passed", "failed"].includes(answer?.state || "");
  const label = translatedName(question, currentUser?.locale);
  const duration = minutesLabel(question.length_minutes);
  const durationText =
    state === "await"
      ? `${duration} · awaiting marking`
      : state === "failed"
        ? `${duration} · Lesson failed`
        : duration;
  const className = `lesson lesson--${state}${lessonLocked ? " is-locked" : ""}`;
  const ariaLabel = `Lesson ${index + 1}: ${label}. ${duration}. ${
    lessonLocked ? "Locked" : lessonStatusLabel(state)
  }`;
  const content = (
    <>
      <LessonIcon
        enrolment={enrolment}
        questionNumber={index + 1}
        state={state}
      />
      <span className="tip">
        {label}
        {durationText && <span className="dur">{durationText}</span>}
        {lessonLocked && <span className="dur">Locked</span>}
      </span>
    </>
  );

  if (lessonLocked) {
    return (
      <span
        className={className}
        aria-disabled="true"
        aria-label={ariaLabel}
        role="link"
      >
        {content}
      </span>
    );
  }

  return (
    <Link
      className={className}
      to={questionPath(enrolment?.id, question, answers)}
      aria-current={state === "current" ? "page" : undefined}
      aria-label={ariaLabel}
    >
      {content}
    </Link>
  );
};

const Chapter = ({
  answers,
  chapter,
  currentUser,
  enrolment,
  locked,
}: {
  answers: Answer[];
  chapter: TopicPreview;
  currentUser: any;
  enrolment: Enrolment;
  locked: boolean;
}) => {
  const complete = chapter.completed;

  return (
    <article
      className={`chapter ${complete ? "is-complete" : ""}${
        locked ? " is-locked" : ""
      }`}
    >
      <div className="chapter__node">
        {String(chapter.position + 1).padStart(2, "0")}
        <span className="chapter__badge" aria-hidden="true">
          <svg>
            <use href="#ic-check" />
          </svg>
        </span>
      </div>
      <div className="chapter__head">
        <h2 className="chapter__title">
          {translatedName(chapter, currentUser?.locale)}
        </h2>
        <div className="chapter__right">
          <span className={`pill ${complete ? "is-done" : ""}`}>
            {chapter.answer_count} of {chapter.question_count} done
          </span>
        </div>
      </div>
      <p className="chapter__desc">
        {translatedDescription(chapter, currentUser?.locale)}
      </p>
      <div className="lessons">
        {chapter.questions.map((question, index) => (
          <React.Fragment key={question.id}>
            <Lesson
              answers={answers}
              currentUser={currentUser}
              enrolment={enrolment}
              index={index}
              locked={locked}
              question={question}
            />
            {index < chapter.questions.length - 1 && (
              <span className="lconn"></span>
            )}
          </React.Fragment>
        ))}
      </div>
    </article>
  );
};

const LessonLegend = () => (
  <div className="legend" aria-label="What the lesson colours mean">
    <span className="li">
      <span className="dot todo">1</span> Not taken
    </span>
    <span className="li">
      <span className="dot passed">
        <svg aria-hidden="true">
          <use href="#ic-check" />
        </svg>
      </span>{" "}
      Passed
    </span>
    <span className="li">
      <span className="dot await">
        <svg aria-hidden="true">
          <use href="#ic-clock" />
        </svg>
      </span>{" "}
      Awaiting marking
    </span>
    <span className="li">
      <span className="dot failed">
        <svg aria-hidden="true">
          <use href="#ic-retry" />
        </svg>
      </span>{" "}
      Lesson failed
    </span>
  </div>
);

const ChaptersPanel = ({
  activeTab,
  answers,
  chapters,
  currentUser,
  enrolment,
  loading,
}: Props) => (
  <section
    className="tabpanel"
    id="panel-chapters"
    role="tabpanel"
    aria-labelledby="tab-chapters"
    hidden={activeTab !== "chapters"}
  >
    {loading ? (
      <div className="ui active inverted dimmer">
        <div className="ui text loader">Loading</div>
      </div>
    ) : (
      <div className="timeline">
        {chapters.map((chapter) => (
          <Chapter
            answers={answers}
            chapter={chapter}
            currentUser={currentUser}
            enrolment={enrolment}
            key={chapter.id}
            locked={!!enrolment?.locked}
          />
        ))}
      </div>
    )}

    <LessonLegend />
  </section>
);

export default ChaptersPanel;
